import { useCallback, useRef, useState, type ReactNode } from 'react'
import { Button, Modal } from '@indipia/ui-core'

// 착공 D-2 이내 수동 배정 즉시 확정 확인 (WP4 3단계).
// 서버가 수동 배정(PATCH /tasks/{id}·일괄 PATCH·partner-changes)에서 착공 D-2 이내이면
// 409 IMMEDIATE_CONFIRM_REQUIRED(쓰기 없음)를 준다. 이 훅은 그 응답을 받으면 확인 모달을 띄우고,
// 확정하면 같은 요청을 confirmImmediate=true 로 다시 호출한다. 취소하면 호출하지 않고 cancelled 를 돌려준다.

export const IMMEDIATE_CONFIRM_REQUIRED = 'IMMEDIATE_CONFIRM_REQUIRED'

/** 즉시 확정 성공 토스트 문구 */
export const IMMEDIATE_CONFIRM_SUCCESS_MESSAGE = '일정을 확정하고 알려드렸어요'

/** 서버 data.immediateDays 로 조립한 안내 문구 — data 가 없으면 서버 message 그대로 */
function composeMessage(immediateDays: number | undefined, serverMessage: string | undefined | null): string {
  if (typeof immediateDays === 'number') return `착공 D-${immediateDays} 이내라 일정확인 없이 확정 처리됩니다.`
  return serverMessage || '착공이 임박해 일정확인 없이 확정 처리됩니다.'
}

/** 409 IMMEDIATE_CONFIRM_REQUIRED 이면 확인 모달 본문(및 D-n), 아니면 null. axios 오류·ForemanChangeError 모두 처리. */
export function immediateConfirmMessage(err: unknown): string | null {
  const e = err as {
    code?: string; serverMessage?: string | null; serverData?: { immediateDays?: number } | null
    response?: { status?: number; data?: { code?: string; message?: string; data?: { immediateDays?: number } | null } }
  }
  if (e?.code === IMMEDIATE_CONFIRM_REQUIRED) return composeMessage(e.serverData?.immediateDays, e.serverMessage)
  const res = e?.response
  if (res?.status === 409 && res.data?.code === IMMEDIATE_CONFIRM_REQUIRED) {
    return composeMessage(res.data.data?.immediateDays, res.data.message)
  }
  return null
}

export type ImmediateRunResult<T> = { cancelled: false; value: T } | { cancelled: true }

interface AskState { message: string; resolve: (ok: boolean) => void }

/**
 * run(call): call(false) 를 호출하고, 즉시 확정 확인이 필요하다는 409 면 모달로 묻는다.
 * 확정하면 call(true) 로 다시 호출한 결과를, 취소하면 { cancelled: true } 를 돌려준다. 다른 오류는 그대로 던진다.
 * modal 은 호출하는 컴포넌트가 JSX 안에 한 번 렌더한다.
 */
export function useImmediateConfirm() {
  const [ask, setAsk] = useState<AskState | null>(null)
  const askRef = useRef<AskState | null>(null)
  askRef.current = ask

  const confirm = useCallback((message: string) => new Promise<boolean>(resolve => {
    setAsk({ message, resolve })
  }), [])

  const close = (ok: boolean) => {
    const a = askRef.current
    setAsk(null)
    a?.resolve(ok)
  }

  const run = useCallback(async <T,>(call: (confirmImmediate: boolean) => Promise<T>): Promise<ImmediateRunResult<T>> => {
    try {
      return { cancelled: false, value: await call(false) }
    } catch (err) {
      const msg = immediateConfirmMessage(err)
      if (msg == null) throw err
      if (!(await confirm(msg))) return { cancelled: true }
      return { cancelled: false, value: await call(true) }
    }
  }, [confirm])

  const dDay = ask ? /D-(\d+)/.exec(ask.message)?.[1] : null
  const modal: ReactNode = (
    <Modal
      open={ask != null}
      onClose={() => close(false)}
      title={dDay ? `착공 D-${dDay} 이내예요` : '착공이 임박했어요'}
      footerAlign="stretch"
      footer={ask && (
        <>
          <Button variant="secondary" onClick={() => close(false)}>취소</Button>
          <Button variant="primary" onClick={() => close(true)}>확정하기</Button>
        </>
      )}
    >
      {ask && (
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--shell-on-surface)' }}>{ask.message}</div>
      )}
    </Modal>
  )

  return { run, modal }
}

/** PATCH /schedule/tasks/{id} 응답이 즉시 확정(partnerStatus=CONFIRMED)인지 — 응답 본문(공종) 기준. */
export function isFixedPatchResponse(res: unknown): boolean {
  const d = (res as { data?: { data?: { partnerStatus?: string } } } | null | undefined)?.data?.data
  return d?.partnerStatus === 'CONFIRMED'
}
